import * as React from 'react';
import { Star, ChevronRight, ChevronDown } from 'lucide-react';

export type SideNavItemTier = 1 | 2 | 3 | 4 | 5;

export interface SideNavItemProps {
  /**
   * The text label for the navigation item
   */
  label: string;
  /**
   * The tier/depth level (1-5). Each tier adds 12px indentation.
   * Tier 1 = 24px left padding (base)
   * @default 1
   */
  tier?: SideNavItemTier;
  /**
   * Whether the item is currently active/selected
   * @default false
   */
  active?: boolean;
  /**
   * Whether the item has child items (shows expand/collapse chevron)
   * @default false
   */
  hasChildren?: boolean;
  /**
   * Whether the item is expanded (only relevant when hasChildren is true)
   * @default false
   */
  expanded?: boolean;
  /**
   * Whether the item is favourited (star is filled)
   * @default false
   */
  favourited?: boolean;
  /**
   * Whether the item is focused (shows green ring)
   * @default false
   */
  focused?: boolean;
  /**
   * Click handler for the item
   */
  onClick?: () => void;
  /**
   * Click handler for the expand/collapse chevron
   */
  onExpandClick?: () => void;
  /**
   * Click handler for the favourite star
   */
  onFavouriteClick?: () => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
}

/**
 * SideNavItem component - Arbor Design System
 *
 * A navigation list item used in the side navigation panel.
 * Supports multiple tiers for nested navigation, favourite functionality,
 * and expand/collapse for parent items with children.
 */
export const SideNavItem = React.forwardRef<HTMLDivElement, SideNavItemProps>(
  (
    {
      label,
      tier = 1,
      active = false,
      hasChildren = false,
      expanded = false,
      favourited = false,
      focused = false,
      onClick,
      onExpandClick,
      onFavouriteClick,
      className,
      style,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isStarHovered, setIsStarHovered] = React.useState(false);

    // Calculate left padding based on tier
    // Tier 1 = 24px, each additional tier adds 12px
    // If hasChildren, reduce padding to make room for chevron
    const basePadding = 24;
    const tierIndent = (tier - 1) * 12;
    const chevronSpace = hasChildren ? 20 : 0;
    const leftPadding = basePadding + tierIndent - chevronSpace;

    // Determine text color based on state
    const getTextColor = () => {
      if (active) return '#005700';
      if (isHovered) return '#2f2f2f';
      return '#333333';
    };

    // Determine background color based on state
    const getBackgroundColor = () => {
      if (active) return '#f0faf3';
      if (isHovered) return '#f8f8f8';
      return 'transparent';
    };

    // Determine font weight
    const getFontWeight = () => {
      return active ? 600 : 400;
    };

    // Show star when hovered or active
    const showStar = isHovered || active;

    // Star icon color
    const getStarColor = () => {
      if (favourited) return '#0e8a0e';
      if (isStarHovered) return '#2f2f2f';
      return '#0e8a0e';
    };

    const containerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: '168px',
      paddingTop: '8px',
      paddingBottom: '8px',
      paddingLeft: `${leftPadding}px`,
      paddingRight: '8px',
      borderRadius: '99px',
      backgroundColor: getBackgroundColor(),
      boxShadow: focused ? '0px 0px 0px 3px #3cad51' : 'none',
      cursor: 'pointer',
      boxSizing: 'border-box',
      transition: 'background-color 150ms ease',
      outline: 'none',
      ...style,
    };

    const chevronStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '16px',
      height: '16px',
      marginLeft: '-4px',
    };

    const labelStyles: React.CSSProperties = {
      flex: 1,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: '13px',
      fontWeight: getFontWeight(),
      color: getTextColor(),
      lineHeight: 1.5,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    const starContainerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '16px',
      height: '16px',
      opacity: showStar ? 1 : 0,
      transition: 'opacity 150ms ease',
    };

    const handleClick = () => {
      onClick?.();
    };

    const handleChevronClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onExpandClick?.();
    };

    const handleStarClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onFavouriteClick?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    };

    return (
      <div
        ref={ref}
        className={className}
        style={containerStyles}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsStarHovered(false);
        }}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={hasChildren ? expanded : undefined}
        aria-label={label}
      >
        {/* Expand/Collapse Chevron */}
        {hasChildren && (
          <div
            style={chevronStyles}
            onClick={handleChevronClick}
            role="button"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? (
              <ChevronDown size={14} color="#7e7e7e" strokeWidth={2} />
            ) : (
              <ChevronRight size={14} color="#7e7e7e" strokeWidth={2} />
            )}
          </div>
        )}

        {/* Label */}
        <span style={labelStyles}>{label}</span>

        {/* Favourite Star */}
        <div
          style={starContainerStyles}
          onClick={handleStarClick}
          onMouseEnter={() => setIsStarHovered(true)}
          onMouseLeave={() => setIsStarHovered(false)}
          role="button"
          aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={favourited}
        >
          <Star
            size={15}
            color={getStarColor()}
            fill={favourited ? '#0e8a0e' : 'none'}
            strokeWidth={2}
          />
        </div>
      </div>
    );
  }
);

SideNavItem.displayName = 'SideNavItem';
